// src/components/DigestSettingsTab.jsx
// Admin tab for configuring daily digest + instant notification email settings

import React, { useState, useEffect } from 'react';
import { Loader2, Mail, Clock, Users, AlertCircle, Check, FileText, ClipboardList } from 'lucide-react';
import { useDigestSettings, useTeam } from '../hooks/useSupabase';

// ── Reusable recipient picker ──────────────────────────────────────────────
function RecipientPicker({ team, secondaryColor, primaryColor, selectedIds, onChange }) {
  const toggleRecipient = (userId) => {
    onChange(
      selectedIds.includes(userId)
        ? selectedIds.filter(id => id !== userId)
        : [...selectedIds, userId]
    );
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-sm font-medium">
          <Users className="w-4 h-4 inline mr-1" />
          Recipients
        </label>
        <div className="flex gap-2 text-xs">
          <button
            onClick={() => onChange(team.filter(m => m.role === 'admin').map(m => m.id))}
            className="text-blue-600 hover:underline"
          >
            Admins only
          </button>
          <span style={{ color: secondaryColor }}>|</span>
          <button onClick={() => onChange(team.map(m => m.id))} className="text-blue-600 hover:underline">
            All
          </button>
          <span style={{ color: secondaryColor }}>|</span>
          <button onClick={() => onChange([])} className="text-blue-600 hover:underline">
            None
          </button>
        </div>
      </div>

      <div className="border rounded-lg overflow-hidden">
        {team.length === 0 ? (
          <div className="p-4 text-center text-sm" style={{ color: secondaryColor }}>
            No team members to select
          </div>
        ) : (
          team.map((member, idx) => (
            <label
              key={member.id}
              className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-gray-50 ${idx > 0 ? 'border-t' : ''}`}
            >
              <input
                type="checkbox"
                checked={selectedIds.includes(member.id)}
                onChange={() => toggleRecipient(member.id)}
                className="w-4 h-4 rounded"
                style={{ accentColor: primaryColor }}
              />
              <div className="flex-1">
                <div className="text-sm font-medium">{member.full_name || member.email}</div>
                <div className="text-xs" style={{ color: secondaryColor }}>{member.email}</div>
              </div>
              <span
                className={`text-xs px-2 py-0.5 rounded ${
                  member.role === 'admin'  ? 'bg-purple-100 text-purple-700' :
                  member.role === 'viewer' ? 'bg-gray-100 text-gray-600' :
                  'bg-blue-100 text-blue-700'
                }`}
              >
                {member.role}
              </span>
            </label>
          ))
        )}
      </div>

      {selectedIds.length === 0 && (
        <div className="flex items-center gap-2 mt-2 text-sm text-orange-600">
          <AlertCircle className="w-4 h-4" />
          No recipients selected — notifications will be skipped
        </div>
      )}
    </div>
  );
}

// ── Toggle row ─────────────────────────────────────────────────────────────
function ToggleRow({ icon: Icon, label, description, checked, onChange, primaryColor, secondaryColor }) {
  return (
    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
      <div className="flex items-center gap-3">
        <Icon className="w-5 h-5" style={{ color: primaryColor }} />
        <div>
          <div className="font-medium">{label}</div>
          <div className="text-sm" style={{ color: secondaryColor }}>{description}</div>
        </div>
      </div>
      <label className="relative inline-flex items-center cursor-pointer">
        <input
          type="checkbox"
          checked={checked}
          onChange={e => onChange(e.target.checked)}
          className="sr-only peer"
        />
        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
      </label>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────
export default function DigestSettingsTab({ organizationId, primaryColor, secondaryColor, showToast }) {
  const { settings, loading, updateSettings } = useDigestSettings(organizationId);
  const { team, loading: teamLoading } = useTeam(organizationId);

  const [saving, setSaving] = useState(false);
  const [localSettings, setLocalSettings] = useState({
    is_enabled: false,
    recipient_user_ids: [],
    send_time: '13:00:00',
    doc_notifications_enabled: true,
    doc_notification_recipient_ids: [],
    info_notifications_enabled: true,
    info_notification_recipient_ids: [],
  });

  useEffect(() => {
    if (settings) {
      setLocalSettings({
        is_enabled:                      settings.is_enabled                      || false,
        recipient_user_ids:              settings.recipient_user_ids              || [],
        send_time:                       settings.send_time                       || '13:00:00',
        doc_notifications_enabled:       settings.doc_notifications_enabled       !== false,
        doc_notification_recipient_ids:  settings.doc_notification_recipient_ids  || settings.recipient_user_ids || [],
        info_notifications_enabled:      settings.info_notifications_enabled      !== false,
        info_notification_recipient_ids: settings.info_notification_recipient_ids || settings.recipient_user_ids || [],
      });
    }
  }, [settings]);

  const handleSave = async () => {
    if (localSettings.is_enabled && localSettings.recipient_user_ids.length === 0) {
      showToast('Daily digest needs at least one recipient', 'error');
      return;
    }
    if (localSettings.doc_notifications_enabled && localSettings.doc_notification_recipient_ids.length === 0) {
      showToast('Document upload notifications need at least one recipient', 'error');
      return;
    }
    if (localSettings.info_notifications_enabled && localSettings.info_notification_recipient_ids.length === 0) {
      showToast('Business info notifications need at least one recipient', 'error');
      return;
    }

    setSaving(true);
    try {
      await updateSettings(localSettings);
      showToast('Notification settings saved');
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading || teamLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: primaryColor }} />
      </div>
    );
  }

  return (
    <div className="space-y-8">

      {/* ── Section 1: Document Upload Notifications ── */}
      <div>
        <h2 className="text-lg font-semibold mb-1" style={{ color: primaryColor }}>
          Document Upload Notifications
        </h2>
        <p className="text-sm mb-4" style={{ color: secondaryColor }}>
          Send an immediate email when a seller uploads a document. Delivered within 5 minutes.
        </p>
        <div className="space-y-4">
          <ToggleRow
            icon={FileText}
            label="Enable Document Notifications"
            description="Notify selected recipients when a seller uploads a file"
            checked={localSettings.doc_notifications_enabled}
            onChange={v => setLocalSettings(prev => ({ ...prev, doc_notifications_enabled: v }))}
            primaryColor={primaryColor}
            secondaryColor={secondaryColor}
          />
          {localSettings.doc_notifications_enabled && (
            <RecipientPicker
              team={team}
              selectedIds={localSettings.doc_notification_recipient_ids}
              onChange={ids => setLocalSettings(prev => ({ ...prev, doc_notification_recipient_ids: ids }))}
              primaryColor={primaryColor}
              secondaryColor={secondaryColor}
            />
          )}
        </div>
      </div>

      <hr className="border-gray-200" />

      {/* ── Section 2: Business Info Notifications ── */}
      <div>
        <h2 className="text-lg font-semibold mb-1" style={{ color: primaryColor }}>
          Business Info Notifications
        </h2>
        <p className="text-sm mb-4" style={{ color: secondaryColor }}>
          Send an immediate email when a seller submits a section of the business information form. Delivered within 5 minutes.
        </p>
        <div className="space-y-4">
          <ToggleRow
            icon={ClipboardList}
            label="Enable Business Info Notifications"
            description="Notify selected recipients when a seller submits a business info section"
            checked={localSettings.info_notifications_enabled}
            onChange={v => setLocalSettings(prev => ({ ...prev, info_notifications_enabled: v }))}
            primaryColor={primaryColor}
            secondaryColor={secondaryColor}
          />
          {localSettings.info_notifications_enabled && (
            <RecipientPicker
              team={team}
              selectedIds={localSettings.info_notification_recipient_ids}
              onChange={ids => setLocalSettings(prev => ({ ...prev, info_notification_recipient_ids: ids }))}
              primaryColor={primaryColor}
              secondaryColor={secondaryColor}
            />
          )}
        </div>
      </div>

      <hr className="border-gray-200" />

      {/* ── Section 3: Daily Digest ── */}
      <div>
        <h2 className="text-lg font-semibold mb-1" style={{ color: primaryColor }}>Daily Digest</h2>
        <p className="text-sm mb-4" style={{ color: secondaryColor }}>
          Send a daily email summary of all activity from the previous day.
        </p>
        <div className="space-y-4">
          <ToggleRow
            icon={Mail}
            label="Enable Daily Digest"
            description="Recipients will receive a summary email each morning"
            checked={localSettings.is_enabled}
            onChange={v => setLocalSettings(prev => ({ ...prev, is_enabled: v }))}
            primaryColor={primaryColor}
            secondaryColor={secondaryColor}
          />

          {localSettings.is_enabled && (
            <>
              <div>
                <label className="block text-sm font-medium mb-2">
                  <Clock className="w-4 h-4 inline mr-1" />
                  Send Time (UTC)
                </label>
                <select
                  value={localSettings.send_time}
                  onChange={e => setLocalSettings(prev => ({ ...prev, send_time: e.target.value }))}
                  className="border rounded px-3 py-2 text-sm"
                >
                  <option value="12:00:00">12:00 UTC (7am EST / 8am EDT)</option>
                  <option value="13:00:00">13:00 UTC (8am EST / 9am EDT)</option>
                  <option value="14:00:00">14:00 UTC (9am EST / 10am EDT)</option>
                  <option value="15:00:00">15:00 UTC (10am EST / 11am EDT)</option>
                </select>
                <p className="text-xs mt-1" style={{ color: secondaryColor }}>
                  Summarizes activity from the previous 24 hours
                </p>
              </div>

              <RecipientPicker
                team={team}
                selectedIds={localSettings.recipient_user_ids}
                onChange={ids => setLocalSettings(prev => ({ ...prev, recipient_user_ids: ids }))}
                primaryColor={primaryColor}
                secondaryColor={secondaryColor}
              />

              <div className="bg-blue-50 rounded-lg p-4">
                <h4 className="font-medium text-blue-800 text-sm mb-2">What's included:</h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• New deals created</li>
                  <li>• Deal stage changes</li>
                  <li>• Files uploaded (highlights seller uploads)</li>
                  <li>• Task status updates</li>
                  <li>• Deals moved to trash or restored</li>
                </ul>
              </div>
            </>
          )}
        </div>

        {settings?.last_sent_at && (
          <div className="text-xs mt-3" style={{ color: secondaryColor }}>
            Last digest sent: {new Date(settings.last_sent_at).toLocaleString()}
          </div>
        )}
      </div>

      {/* ── Save ── */}
      <div className="flex justify-end pt-2">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-4 py-2 text-white rounded flex items-center gap-2 hover:opacity-90 disabled:opacity-50"
          style={{ backgroundColor: primaryColor }}
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
          Save Settings
        </button>
      </div>

    </div>
  );
}
